import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
} from "rxjs";
import { environment } from "../../../../environments/environment";
import { faPowerOff, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import {
  AuthService,
  checkDropdownMenuPermissions,
  hasMenuItemAccess,
  IAppDashboard,
  IAuthProvider,
  IDropDownMenuItem,
  IEffectiveMenu,
  IMenuItem,
  IMenuItemPermissions,
  IPluginPermissions,
  IUser,
  IUserRole,
  LayoutMode,
  LayoutModeService,
  MenuPlacementStore,
  ModelService,
  PermissionLoader,
  PluginInjectorService,
  SessionFreshnessService,
  SessionService,
} from "core-client/api";
import { SharedModule } from "../../../shared/shared.module";
import { FaLayersComponent } from "@fortawesome/angular-fontawesome";
import { DashboardSelectMenuComponent, UserAvatarComponent } from "core-plugin";
import { HoverMenuTriggerDirective } from "./hover-menu-trigger.directive";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  imports: [
    SharedModule,
    UserAvatarComponent,
    FaLayersComponent,
    DashboardSelectMenuComponent,
    HoverMenuTriggerDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./header.component.scss"],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly layoutMode = inject(LayoutModeService);
  private readonly menuPlacementStore = inject(MenuPlacementStore);
  private readonly sessionFreshness = inject(SessionFreshnessService);
  isMobileView: boolean = this.layoutMode.isMobile;
  @Input() loggedUser!: IUser;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  appName: string;
  private permissions: IPluginPermissions[] = [];
  currentDashboard: IAppDashboard | undefined;
  hamburgerBadgeTotal$: Observable<number> = of(0);

  constructor(
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    public pluginInjectorService: PluginInjectorService,
    private readonly modelService: ModelService,
    private readonly sessionService: SessionService,
    private readonly translate: TranslateService,
    private readonly permissionLoader: PermissionLoader,
  ) {
    this.appName = environment.appTitle ? environment.appTitle : "ADDboard";
  }

  onMainMenuOpened(): void {
    void this.sessionFreshness.check();
  }

  ngOnInit(): void {
    this.layoutMode.mode$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((mode: LayoutMode) => {
        this.isMobileView = mode === "mobile";
      });

    this.menuPlacementStore
      .observeEffectiveMenu$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async ({ dashboard, menu }: IEffectiveMenu) => {
        this.currentDashboard = dashboard;
        const user = this.sessionService.user.getUser();
        if (!user) return;
        this.permissions = await this.permissionLoader.load(dashboard, user);
        this.pluginInjectorService.loadPluginMenuItems(menu);
        this.hamburgerBadgeTotal$ = this.buildHamburgerBadgeTotal$();
      });

    this.modelService._userRole
      .observeRoleModification$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (userRole: { _id: string }) => {
        const user = this.sessionService.user.getUser();
        if (!user) return;
        const ownsRole = user.roles.some(
          (role: IUserRole) => role._id === userRole._id,
        );
        if (!ownsRole) return;

        await this.sessionService.user.refreshFromServer();
        await this.sessionService.dashboard.refreshFromServer();
      });
  }

  logout: {
    name: string;
    callback: () => Promise<void>;
    icon: IconDefinition;
  } = {
    name: "Logout",
    callback: async () => await this.authService.logout(),
    icon: faPowerOff,
  };

  async triggerMenuCallback(
    menu: IDropDownMenuItem | IMenuItem,
    index: number,
    parentMenu?: IMenuItem,
  ): Promise<void> {
    if (menu.callback) {
      const menuId = parentMenu?.nameIdentifier ?? menu.nameIdentifier;
      if (menu.useExternalLink) {
        const exterUserProfileUrl = await this.buildExternLinkUrl(menuId);
        await menu.callback(this.dialog, menuId, exterUserProfileUrl);
      } else {
        await menu.callback(this.dialog, menuId);
      }
    }
  }

  async buildExternLinkUrl(menuId: string): Promise<string> {
    let externalLink = "";
    if (menuId === "USER_PROFILE") {
      const user = this.sessionService.user.getUser();
      if (!user) return "";
      const authProviderDetails = user.auth;
      if (authProviderDetails.length === 1) {
        const populateAuthSpecs = authProviderDetails[0];
        const authProvider = await this.modelService._authProvider.getById(
          populateAuthSpecs.provider._id,
        );
        if (authProvider) {
          externalLink = this.createAuthProviderUserProfileLink(authProvider);
        }
      }
    }

    return externalLink;
  }

  createAuthProviderUserProfileLink(authProvider: IAuthProvider): string {
    const endpointUrl = authProvider.config.endpoint_url;
    return `${endpointUrl}/account/`;
  }

  getAltIcon(menu: IMenuItem): false | IconDefinition | undefined {
    return (
      (menu.altIconActive ? menu.altIconActive.value : false) && menu.altIcon
    );
  }

  hasMenuAccess(menu: IMenuItem): boolean {
    return hasMenuItemAccess(menu, this.permissions);
  }

  canAccessDropdownMenu(menu: IMenuItem, dropdown: IDropDownMenuItem): boolean {
    if (!dropdown.accessReference) {
      return true; // No access rights required, allow by default
    }

    for (const pluginPermission of this.permissions) {
      if (
        pluginPermission.plugin_id === menu.pluginId &&
        pluginPermission.menuitem_permissions
      ) {
        if (
          this.hasRequiredDropdownMenuAccess(
            pluginPermission.menuitem_permissions,
            dropdown,
          )
        ) {
          return true; // Exit early if access is found
        }
      }
    }
    return false;
  }

  /** True when the menu has at least one accessible dropdown action besides the dashboard-select panel. */
  hasDropdownActionsBelowPanel(menu: IMenuItem): boolean {
    return (menu.dropdownItems ?? []).some(
      (item: IDropDownMenuItem) =>
        item.customPanel !== "dashboard-select" &&
        this.canAccessDropdownMenu(menu, item),
    );
  }

  // Verification if dropdown menu required permissions are set
  private hasRequiredDropdownMenuAccess(
    menuItemPermissions: IMenuItemPermissions,
    dropdown: IDropDownMenuItem,
  ): boolean {
    // Ensure pluginModule is defined
    if (!dropdown.pluginModule) {
      return false;
    }

    const moduleKey = dropdown.pluginModule.toLowerCase();

    // Only get permissions for this specific module
    const modulePermissions = menuItemPermissions[moduleKey];

    if (!modulePermissions) {
      return false; // No permissions found for this module
    }

    // Validate that accessReference permissions belong to this module
    if (typeof dropdown.accessReference === "string") {
      // Single string reference
      if (!this.permissionExistsInModule(dropdown.accessReference, dropdown)) {
        return false;
      }
    } else if (Array.isArray(dropdown.accessReference)) {
      // Array of references
      const allPermissionsValid = dropdown.accessReference.every((ref) =>
        this.permissionExistsInModule(ref, dropdown),
      );

      if (!allPermissionsValid) {
        return false;
      }
    }

    return checkDropdownMenuPermissions(
      modulePermissions,
      dropdown,
      this.currentDashboard,
    );
  }

  // Helper method to verify permission belongs to module
  private permissionExistsInModule(
    permissionRef: string,
    dropdown: IDropDownMenuItem,
  ): boolean {
    if (!dropdown.pluginModule) {
      return false;
    }

    const permissionLower = permissionRef.toLowerCase();
    const pluginNameLower = dropdown.pluginName.toLowerCase();
    const moduleLower = dropdown.pluginModule.toLowerCase();

    // Permission should contain: {plugin_name}_{module}_
    // e.g., "core_plugin_dashboard_" or "my_custom_plugin_reports_"
    const expectedPattern = `${pluginNameLower}_${moduleLower}_`;
    return permissionLower.includes(expectedPattern);
  }

  /** Resolves the accessible label / tooltip text for a primary menu item. */
  getMenuAriaLabel(menu: IMenuItem): string {
    if (this.getAltIcon(menu) && menu.altName) {
      return this.translate.instant(
        `${menu.pluginName}.${menu.pluginModule}.${menu.altName}`,
      );
    }
    if (menu.pluginModule) {
      return this.translate.instant(
        `${menu.pluginName}.${menu.pluginModule}.${menu.nameIdentifier}`,
      );
    }
    return this.translate.instant(`${menu.pluginName}.${menu.nameIdentifier}`);
  }

  private buildHamburgerBadgeTotal$(): Observable<number> {
    const menu = this.pluginInjectorService.getMenu();
    const badges = [...menu.primary, ...menu.secondary]
      .filter((m: IMenuItem) => this.hasMenuAccess(m) && m.badge$)
      .map((m: IMenuItem) => m.badge$ as BehaviorSubject<number>);
    if (badges.length === 0) {
      return of(0);
    }
    return combineLatest(badges).pipe(
      map((counts: number[]) =>
        counts.reduce((sum: number, count: number) => sum + count, 0),
      ),
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
